import type { MergeStatus } from "@/common/lib/api/visitMerges";

export const STATUS_POLL_MS = 3_000;
export const STATUS_POLL_FOR_MS = 60_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type Options = {
    /** fetchMergeStatus (by idempotency key) or fetchMergeStatusById, bound to its argument. */
    fetch: () => Promise<MergeStatus | null>;
    /** The status that proves the merge or unmerge happened. */
    want: MergeStatus["status"];
    /** Keep asking while no merge is found yet (a merge whose row may not be committed yet). */
    waitWhileMissing?: boolean;
    /** Keep asking while the merge still shows the other status (an unmerge that may still be in flight). */
    waitWhileOther?: boolean;
    /** Keep asking after a failed status request. */
    retryOnError?: boolean;
};

/**
 * Asks the server how a merge or unmerge ended before the caller claims anything: every STATUS_POLL_MS for up
 * to STATUS_POLL_FOR_MS while a wait option says the answer may still change, otherwise once. Returns the
 * status when it shows `want`, else null (which proves nothing either way).
 */
export async function pollMergeStatus({
    fetch,
    want,
    waitWhileMissing = false,
    waitWhileOther = false,
    retryOnError = false,
}: Options): Promise<MergeStatus | null> {
    const giveUpAt = Date.now() + STATUS_POLL_FOR_MS;
    for (;;) {
        let status: MergeStatus | null | undefined;
        try {
            status = await fetch();
        } catch {
            status = undefined;
        }
        if (status?.status === want) return status;
        const keepWaiting = status === undefined ? retryOnError : status === null ? waitWhileMissing : waitWhileOther;
        if (!keepWaiting || Date.now() + STATUS_POLL_MS > giveUpAt) return null;
        await sleep(STATUS_POLL_MS);
    }
}
